import React, { useEffect, useState, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './theme.css';
import './App.css';
import JobDetails from './JobDetails';
import JobConflicts from './JobConflicts'; // OLD - Sprint 20: To be removed after testing
import JobConflictsTableView from './JobConflictsTableView'; // NEW - Sprint 20: Refactored with DataTable
import NewSources from './NewSources'; // OLD - Sprint 20: To be removed after testing
import NewSourcesTableView from './NewSourcesTableView'; // NEW - Sprint 20: Refactored with DataTable
import AIMonitoring from './AIMonitoring';
import CitiesList from './CitiesList'; // OLD - Sprint 20: To be removed after testing
import CitiesTableView from './CitiesTableView'; // NEW - Sprint 20: Refactored with DataTable
import ContactsList from './ContactsList'; // OLD - Sprint 20: Keep for import wizard (complex form UI)
import ContactsTableView from './ContactsTableView'; // NEW - Sprint 20: Refactored with DataTable
import CityProfile from './CityProfile';
import CalendarView from './CalendarView';
import ContactProfile from './ContactProfile';
import UserManagement from './UserManagement';  // Sprint 10
import AuditLogViewer from './AuditLogViewer';  // Sprint 10
import ChangePassword from './ChangePassword';  // Sprint 10
import NotificationSettings from './NotificationSettings';  // Sprint 14
import CompanyCities from './CompanyCities';  // Sprint 16: Multi-Tenant SaaS
import Login from './Login';  // Sprint 10.1
import OnboardingWizard from './OnboardingWizard';  // Phase 2: Beta Rollout
import AcceptInvitation from './AcceptInvitation';  // Sprint 10.2: User Invitations
import RequestPasswordReset from './RequestPasswordReset';  // Sprint 10.2: Password Reset
import ResetPassword from './ResetPassword';  // Sprint 10.2: Password Reset
import ForcePasswordChange from './ForcePasswordChange';  // Sprint 10.2: Force Password Change
import API_BASE_URL from './config';
import { WORK_TYPES } from './constants';
// Sprint 20: Table Refactoring
import RFQTableView from './RFQTableView';

function App() {
    // Sprint 10.1: Session-based authentication
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [rfqs, setRfqs] = useState([]);
    const [cities, setCities] = useState([]);
    const [newCity, setNewCity] = useState({ organization: '', url: '', row_selector: '', cell_count: 4, is_dynamic: false, manual: false });
    const [pasteText, setPasteText] = useState('');
    const [pasteUrl, setPasteUrl] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotOrg, setScreenshotOrg] = useState('');

    // City detection states
    const [detectionLoading, setDetectionLoading] = useState(false);
    const [detectedConfig, setDetectedConfig] = useState(null);
    const [showConfigModal, setShowConfigModal] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        workType: 'all',
        userStatus: 'all',
        organization: 'all',
        source: 'all',  // Filter by source: all, scraper, email, manual
        searchTerm: '',
        hideIgnored: true,  // Hide ignored by default
        sortBy: 'due_date'  // Default sort by due date
    });
    
    // Separate state for search input (updates immediately, no lag)
    const [searchInput, setSearchInput] = useState('');
    
    // Debounce search: only apply filter after user stops typing
    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, searchTerm: searchInput }));
        }, 300); // 300ms delay
        
        return () => clearTimeout(timer);
    }, [searchInput]);
    
    // Sprint 13: Removed state-based navigation (selectedJobId, currentView, selectedCity, selectedContactId)
    // Now using React Router URL-based navigation
    const [healthData, setHealthData] = useState(null);
    
    // Theme state
    const [theme, setTheme] = useState(() => {
        // Get theme from localStorage or default to 'light'
        return localStorage.getItem('rfq-theme') || 'light';
    });
    
    // Sprint 10: User role state
    const [currentUser, setCurrentUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [companySettings, setCompanySettings] = useState(null);

    // Sprint 15: Sidebar navigation state
    const [sidebarOpen, setSidebarOpen] = useState(false);

    // Sprint 13: React Router navigation (must be declared at top level, before any returns)
    const navigate = useNavigate();
    
    // Sprint 10.2: Get current location to check for public routes
    const location = useLocation();
    
    // Track previous location for navigation detection
    const [prevPath, setPrevPath] = React.useState(location.pathname);
    
    // Ref to maintain focus on search input during re-renders
    const searchInputRef = useRef(null);

    // Function to fetch RFQs (can be called from multiple places)
    const fetchRfqs = () => {
        fetch(`${API_BASE_URL}/rfqs`, {
            credentials: 'include'  // Include HTTP Basic Auth credentials
        })
            .then(res => res.json())
            .then(data => {
                console.log('RFQs:', data);
                // Ensure data is an array before setting
                if (Array.isArray(data)) {
                setRfqs(data);
                } else {
                    console.error('API returned non-array data:', data);
                    setRfqs([]);
                }
            })
            .catch(err => {
                console.error('Error fetching RFQs:', err);
                setRfqs([]);
            });
    };

    // Apply theme on mount and when theme changes
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('rfq-theme', theme);
    }, [theme]);
    
    // Auto-refresh RFQ list when navigating back to home page
    useEffect(() => {
        if (location.pathname === '/' && prevPath !== '/') {
            console.log('Navigated back to RFQ list - refreshing data');
            fetchRfqs();
        }
        setPrevPath(location.pathname);
    }, [location.pathname]);

    // Sprint 10.1: Check authentication on mount
    useEffect(() => {
        checkAuthentication();
    }, []);

    const checkAuthentication = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/me`, {
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('Current user:', data);
                setCurrentUser(data);
                setUserRole(data.role);
                setIsAuthenticated(true);
                
                // Fetch company settings (including logo)
                try {
                    const companyResponse = await fetch(`${API_BASE_URL}/api/companies/me`, {
                        credentials: 'include'
                    });
                    if (companyResponse.ok) {
                        const companyData = await companyResponse.json();
                        console.log('Company settings:', companyData);
                        setCompanySettings(companyData);
                    }
                } catch (err) {
                    console.error('Failed to fetch company settings:', err);
                }
                
                // Phase 2: Check if admin needs to complete onboarding
                if ((data.role === 'admin' || data.role === 'dev') && data.company_settings) {
                    try {
                        const settings = typeof data.company_settings === 'string' 
                            ? JSON.parse(data.company_settings) 
                            : data.company_settings;
                        
                        if (settings.onboarding_completed === false) {
                            console.log('Onboarding not completed, redirecting to wizard...');
                            navigate('/onboarding');
                        }
                    } catch (e) {
                        console.error('Error parsing company settings:', e);
                    }
                }
                
                // Fetch data after successful auth
                fetchRfqs();
                fetchCities();
                fetchHealthData();
            } else {
                // Not authenticated
                setIsAuthenticated(false);
            }
        } catch (err) {
            console.error('Error checking authentication:', err);
            setIsAuthenticated(false);
        } finally {
            setAuthLoading(false);
        }
    };

    const fetchCities = () => {
        fetch(`${API_BASE_URL}/api/cities`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                console.log('Cities loaded:', data.length);
                setCities(data);
            })
            .catch(err => console.error('Error fetching cities:', err));
    };

    const fetchHealthData = () => {
        fetch(`${API_BASE_URL}/health`, {
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                console.log('Health data:', data);
                setHealthData(data);
            })
            .catch(err => console.error('Error fetching health data:', err));
    };

    const handleLoginSuccess = (user) => {
        setCurrentUser(user);
        setUserRole(user.role);
        setIsAuthenticated(true);
        
        // Fetch data after login
        fetchRfqs();
        fetchCities();
        fetchHealthData();
    };

    const handleLogout = async () => {
        try {
            await fetch(`${API_BASE_URL}/logout`, {
                method: 'POST',
                credentials: 'include'
            });
            
            // Clear state (Sprint 18b: Added companySettings to fix logo caching)
            setIsAuthenticated(false);
            setCurrentUser(null);
            setUserRole(null);
            setCompanySettings(null);  // Clear company logo and settings
            setRfqs([]);
            setCities([]);
            // Sprint 13: No longer need setCurrentView - routing handles navigation
        } catch (err) {
            console.error('Logout error:', err);
        }
    };

    const addCity = async () => {
        // Validate inputs
        if (!newCity.organization || !newCity.url) {
            alert('❌ Please enter both organization name and URL');
            return;
        }
        
        // Step 1: Auto-detect configuration
        setDetectionLoading(true);
        
        try {
            const detectResponse = await fetch(`${API_BASE_URL}/detect_city_config`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                    organization: newCity.organization, 
                    url: newCity.url 
                })
            });
            
            const detectData = await detectResponse.json();
            setDetectionLoading(false);
            
            if (detectData.error) {
                // Detection failed, use fallback config
                alert(`⚠️ Auto-detection had issues:\n${detectData.error}\n\nShowing default configuration for manual review.`);
                setDetectedConfig(detectData.fallback_config || {
                    organization: newCity.organization,
                    url: newCity.url,
                    row_selector: 'tbody tr',
                    cell_count: 4,
                    is_dynamic: false,
                    manual: false,
                    notes: ['Detection failed - please configure manually']
                });
            } else {
                // Detection succeeded, show results
                setDetectedConfig(detectData.config);
            }
            
            // Show modal for user review
            setShowConfigModal(true);
            
        } catch (error) {
            setDetectionLoading(false);
            console.error('Error detecting city config:', error);
            alert('❌ Failed to detect configuration. Please try again or configure manually.');
        }
    };
    
    const confirmAndSaveCity = async (finalConfig) => {
        // Step 2: Save the confirmed configuration
        try {
            const response = await fetch(`${API_BASE_URL}/add_city`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(finalConfig)
            });
            
            const data = await response.json();
            
            if (data.error) {
                // Show error with suggestion if available
                const errorMessage = data.suggestion 
                    ? `❌ ${data.error}\n\n${data.suggestion}`
                    : `❌ ${data.error}`;
                alert(errorMessage);
            } else {
                // Clear form and modal
                setNewCity({ organization: '', url: '', row_selector: '', cell_count: 4, is_dynamic: false, manual: false });
                setDetectedConfig(null);
                setShowConfigModal(false);
                
                // Reload cities from server
                fetch(`${API_BASE_URL}/api/cities`, { credentials: 'include' })
            .then(res => res.json())
                    .then(freshCities => {
                        if (Array.isArray(freshCities)) {
                            setCities(freshCities);
                        }
                    });
                
                // Ask if user wants to scrape now
                const scrapeNow = window.confirm(
                    `✅ ${finalConfig.organization} added successfully!\n\n` +
                    `Total cities: ${data.total_cities}\n\n` +
                    `Would you like to scrape this city now to test the configuration?`
                );
                
                if (scrapeNow) {
                    scrapeCity(finalConfig.organization);
                }
            }
        } catch (error) {
            console.error('Error adding city:', error);
            alert('❌ Failed to add city. Check console for details.');
        }
    };
    
    const scrapeCity = (cityName) => {
        fetch(`${API_BASE_URL}/run_scraper`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ cities: [cityName] })
        })
            .then(res => res.json())
            .then(data => {
                console.log('Scraper response:', data);
                alert(`🔍 Scraper started for ${cityName}!\n\nA command window will open showing progress.\nRefresh the page after scraping completes to see new jobs.`);
            })
            .catch(err => {
                console.error('Error starting scraper:', err);
                alert(`❌ Failed to start scraper for ${cityName}. Check console for details.`);
            });
    };
    
    const handleBulkCityUpload = async (file) => {
        if (!file) return;
        
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await fetch(`${API_BASE_URL}/upload_cities`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            const data = await response.json();
            
            if (data.error) {
                alert(`❌ ${data.error}`);
                return;
            }
            
            // Show detailed results
            let message = `✅ Bulk Upload Complete!\n\n`;
            message += `📊 Summary:\n`;
            message += `• Total Rows: ${data.summary.total_rows}\n`;
            message += `• ✅ Added: ${data.summary.added}\n`;
            message += `• ⏭️ Skipped: ${data.summary.skipped}\n`;
            message += `• ❌ Errors: ${data.summary.errors}\n\n`;
            
            if (data.details.added.length > 0) {
                message += `Cities Added:\n`;
                data.details.added.forEach(item => {
                    message += `  • ${item.organization}\n`;
                });
                message += `\n`;
            }
            
            if (data.details.skipped.length > 0) {
                message += `⏭️ Skipped Cities:\n`;
                data.details.skipped.forEach(item => {
                    message += `  • Row ${item.row}: ${item.organization}\n`;
                    message += `    Reason: ${item.reason}\n`;
                    if (item.detail) {
                        message += `    ${item.detail}\n`;
                    }
                    if (item.suggestion) {
                        message += `    💡 ${item.suggestion}\n`;
                    }
                    message += `\n`;
                });
            }
            
            if (data.details.errors.length > 0) {
                message += `❌ Errors (Fix These):\n`;
                data.details.errors.forEach(item => {
                    message += `  • Row ${item.row}: ${item.organization || '(blank)'}\n`;
                    message += `    Problem: ${item.error}\n\n`;
                });
            }
            
            alert(message);
            
            // Reload cities list
            fetch(`${API_BASE_URL}/api/cities`, { credentials: 'include' })
                .then(res => res.json())
                .then(freshCities => {
                    if (Array.isArray(freshCities)) {
                        setCities(freshCities);
                    }
                });
            
        } catch (error) {
            console.error('Error uploading cities:', error);
            alert('❌ Failed to upload file. Check console for details.');
        }
    };

    const toggleTheme = () => {
        setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
    };

    // Sprint 15: Sidebar navigation functions
    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    const navigateAndCloseSidebar = (path) => {
        navigate(path);
        closeSidebar();
    };

    const runScraper = () => {
        // Check if we're on production (rfq-tracker.com) or localhost
        const isProduction = window.location.hostname === 'rfq-tracker.com';
        
        if (isProduction) {
            // Show helpful instructions for production users
            const proceed = window.confirm(
                '💡 RECOMMENDED WORKFLOW:\n\n' +
                '1. On your DEV LAPTOP, open http://localhost:3000\n' +
                '2. Click "Run Scraper" there (Chrome windows will pop up)\n' +
                '3. Wait 5-10 minutes for scraper to complete\n' +
                '4. Come back here to rfq-tracker.com\n' +
                '5. Click "🔄 Sync Database" button\n' +
                '6. Select your local rfq_tracking.db file\n' +
                '7. Production now has the latest data!\n\n' +
                '⚠️ Running scraper on production is unreliable.\n\n' +
                'Click OK to try running on production anyway,\n' +
                'or Cancel to follow the recommended workflow.'
            );
            
            if (!proceed) {
                return; // User cancelled, don't run scraper
            }
        }
        
        fetch(`${API_BASE_URL}/run_scraper`, { 
            method: 'POST',
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                console.log('Scraper:', data);
                alert('Scraper started! This will take 5-10 minutes. Refresh the page after to see new jobs.');
            })
            .catch(err => console.error('Error running scraper:', err));
    };

    const handleExportToExcel = async () => {
        try {
            // Build URL with current filter parameters
            let apiUrl = `${API_BASE_URL}/export/jobs?`;
            const params = [];
            
            if (filters.userStatus && filters.userStatus !== 'all') {
                params.push(`status=${encodeURIComponent(filters.userStatus)}`);
            }
            if (filters.organization && filters.organization !== 'all') {
                params.push(`city=${encodeURIComponent(filters.organization)}`);
            }
            if (filters.workType && filters.workType !== 'all') {
                params.push(`work_type=${encodeURIComponent(filters.workType)}`);
            }
            if (filters.searchTerm && filters.searchTerm.trim() !== '') {
                params.push(`search=${encodeURIComponent(filters.searchTerm)}`);
            }
            if (filters.hideIgnored) {
                params.push(`hide_ignored=true`);
            }
            
            apiUrl += params.join('&');
            
            const response = await fetch(apiUrl, {
                credentials: 'include'
            });
            
            if (!response.ok) {
                throw new Error('Export failed');
            }
            
            // Get the blob from the response
            const blob = await response.blob();
            
            // Create a download link
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `rfq_jobs_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
            
            // Show success message with filter info
            let message = '✅ Excel file downloaded successfully!';
            if (params.length > 0) {
                message += '\n\nNote: Export includes currently applied filters.';
            }
            alert(message);
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            alert('❌ Failed to export to Excel. Please try again.');
        }
    };

    const parseText = () => {
        if (!screenshotOrg || !pasteText.trim()) {
            alert('Please enter both organization name and text to parse');
            return;
        }
        
        fetch(`${API_BASE_URL}/parse_text`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ 
                text: pasteText, 
                organization: screenshotOrg,
                url: pasteUrl.trim() || ''
            })
        })
            .then(res => res.json())
            .then(data => {
                console.log('Parse result:', data);
                if (data.rfqs && data.rfqs.length > 0) {
                    alert(`✅ Successfully parsed and saved ${data.rfqs.length} job(s)!`);
                    // Reload RFQs from database to get complete data with job_id
                    fetchRfqs();
                    // Clear the input fields
                    setPasteText('');
                    setPasteUrl('');
                } else {
                    alert('⚠️ Could not parse any jobs from the text. Check the format and try again.');
                }
            })
            .catch(err => {
                console.error('Error parsing text:', err);
                alert('❌ Error parsing text. Check console for details.');
            });
    };

    const uploadScreenshot = () => {
        const formData = new FormData();
        formData.append('file', screenshot);
        formData.append('organization', screenshotOrg);
        fetch(`${API_BASE_URL}/upload_screenshot`, {
            method: 'POST',
            credentials: 'include',
            body: formData
        })
            .then(res => res.json())
            .then(data => {
                setRfqs([...rfqs, ...data.rfqs]);
                console.log('Screenshot RFQs:', data);
            })
            .catch(err => console.error('Error uploading screenshot:', err));
    };

    const updateUserStatus = (jobId, status, notes = null) => {
        fetch(`${API_BASE_URL}/update_job_status`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ job_id: jobId, status, notes })
        })
            .then(res => res.json())
            .then(data => {
                // Update local state
                setRfqs(rfqs.map(rfq => 
                    rfq.job_id === jobId ? { ...rfq, user_status: status, user_notes: notes } : rfq
                ));
                console.log('Updated job status:', data);
            })
            .catch(err => console.error('Error updating job status:', err));
    };

    const updateWorkType = (jobId, workType) => {
        fetch(`${API_BASE_URL}/update_work_type`, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ job_id: jobId, work_type: workType })
        })
            .then(res => res.json())
            .then(data => {
                // Update local state
                setRfqs(rfqs.map(rfq => 
                    rfq.job_id === jobId ? { ...rfq, work_type: workType } : rfq
                ));
                console.log('Updated work type:', data);
            })
            .catch(err => console.error('Error updating work type:', err));
    };

    const syncDatabase = () => {
        // Show reminder about which file to use
        if (!window.confirm(
            '📁 Sync Database from Dev to Production\n\n' +
            '✅ Select file: rfq_tracking.db\n' +
            '📍 Location: dev laptop at civil-eng-rfq-app/rfq_scraper/\n\n' +
            'This will:\n' +
            '• Add NEW jobs from dev\n' +
            '• Update job metadata\n' +
            '• PRESERVE all status, notes, contacts\n' +
            '• NOT sync cities (add cities manually to both dev and prod)\n\n' +
            'Continue?'
        )) {
            return;
        }

        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.db';
        fileInput.style.display = 'none';
        document.body.appendChild(fileInput);
        
        fileInput.onchange = async (e) => {
            // Close sidebar now that user has selected a file
            closeSidebar();
            
            const file = e.target.files[0];
            if (!file) {
                document.body.removeChild(fileInput);
                return;
            }

            if (!window.confirm(`Upload database file "${file.name}" to server?\n\nThis will:\n• Add NEW jobs from your dev database\n• Update job metadata (title, link, due date)\n• PRESERVE all your notes and status changes\n• Create a backup before syncing\n\nContinue?`)) {
                document.body.removeChild(fileInput);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            try {
                const response = await fetch(`${API_BASE_URL}/sync_database`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData
                });

                const result = await response.json();
                
                if (result.success) {
                    alert(`✅ ${result.message}`);
                    // Refresh the RFQs list
                    fetchRfqs();
                } else {
                    alert(`❌ Sync failed: ${result.error}`);
                }
            } catch (error) {
                alert(`❌ Sync failed: ${error.message}`);
            }
            
            // Clean up
            document.body.removeChild(fileInput);
        };
        
        // Also close sidebar if user cancels the file dialog
        fileInput.oncancel = () => {
            closeSidebar();
            document.body.removeChild(fileInput);
        };
        
        fileInput.click();
    };

    // Apply filters
    const filteredRfqs = rfqs.filter(rfq => {
        // Filter by work type
        if (filters.workType !== 'all' && rfq.work_type !== filters.workType) return false;
        
        // Filter by user status
        if (filters.userStatus !== 'all' && rfq.user_status !== filters.userStatus) return false;
        
        // Filter by organization
        if (filters.organization !== 'all' && rfq.organization !== filters.organization) return false;
        
        // Filter by source (email, scraper, manual)
        if (filters.source !== 'all') {
            const jobSource = rfq.source || 'scraper'; // Default to scraper if not set
            if (jobSource !== filters.source) return false;
        }
        
        // Hide ignored jobs (if checked)
        if (filters.hideIgnored && rfq.user_status === 'ignore') return false;
        
        // Filter by search term
        if (filters.searchTerm) {
            const searchLower = filters.searchTerm.toLowerCase();
            const matchesSearch = 
                rfq.title?.toLowerCase().includes(searchLower) ||
                rfq.rfp_number?.toLowerCase().includes(searchLower) ||
                rfq.organization?.toLowerCase().includes(searchLower);
            if (!matchesSearch) return false;
        }
        
        return true;
    }).sort((a, b) => {
        // Apply sorting
        switch (filters.sortBy) {
            case 'due_date':
                // Parse dates for comparison
                const parseDateSafely = (dateStr) => {
                    if (!dateStr || dateStr === 'N/A') return null;
                    try {
                        // Clean up the date string for better parsing
                        let cleanedDate = dateStr
                            .replace(/(\d+)(st|nd|rd|th)/g, '$1') // Remove ordinal suffixes (13th -> 13)
                            .replace(/\s+MST|\s+PST|\s+EST|\s+CST/gi, '') // Remove timezone abbreviations
                            .trim();
                        
                        const date = new Date(cleanedDate);
                        // Check if date is valid
                        if (isNaN(date.getTime())) return null;
                        return date;
                    } catch {
                        return null;
                    }
                };
                
                const dateA = parseDateSafely(a.due_date);
                const dateB = parseDateSafely(b.due_date);
                const now = new Date();
                
                // If both invalid, maintain order
                if (!dateA && !dateB) return 0;
                // Invalid dates go to end
                if (!dateA) return 1;
                if (!dateB) return -1;
                
                // Past dates go to end
                const isPastA = dateA < now;
                const isPastB = dateB < now;
                
                if (isPastA && !isPastB) return 1;  // A past, B future -> B first
                if (!isPastA && isPastB) return -1; // A future, B past -> A first
                
                // Both past or both future, sort normally
                return dateA - dateB;
                
            case 'work_type':
                return (a.work_type || '').localeCompare(b.work_type || '');
            case 'organization':
                return (a.organization || '').localeCompare(b.organization || '');
            case 'date_added':
                // Sort by first_seen (most recent first)
                const addedA = a.first_seen ? new Date(a.first_seen) : new Date(0);
                const addedB = b.first_seen ? new Date(b.first_seen) : new Date(0);
                return addedB - addedA;
            default:
                return 0;
        }
    });

    // Get unique organizations for filter dropdown
    const organizations = [...new Set(rfqs.map(rfq => rfq.organization))].sort();

    // Helper function for due date styling
    const getDueDateStyle = (dueDateStr) => {
        if (!dueDateStr || dueDateStr === 'N/A') return {};
        
        try {
            // Clean up the date string
            let cleanedDate = dueDateStr
                .replace(/(\d+)(st|nd|rd|th)/g, '$1')
                .replace(/\s+MST|\s+PST|\s+EST|\s+CST/gi, '')
                .trim();
            
            const dueDate = new Date(cleanedDate);
            if (isNaN(dueDate.getTime())) return {};
            
            const now = new Date();
            const diffTime = dueDate - now;
            const diffDays = diffTime / (1000 * 60 * 60 * 24);
            
            if (diffDays < 0) {
                // Past due
                return { color: 'red', fontWeight: 'bold' };
            } else if (diffDays <= 7) {
                // Within 1 week
                return { color: 'darkorange', fontWeight: 'bold' };
            } else if (diffDays <= 14) {
                // Within 2 weeks
                return { color: 'orange' };
            }
            
            return {}; // Normal
        } catch {
            return {};
        }
    };

    // Helper function to get city procurement URL
    const getCityUrl = (organizationName) => {
        const city = cities.find(c => c.name === organizationName);
        return city ? city.url : null;
    };

    // Sprint 10.2: Check if current route is a public route (no auth required)
    const publicRoutes = ['/accept-invitation', '/reset-password'];
    const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

    // Sprint 10.1: Show loading or login screen if not authenticated
    if (authLoading && !isPublicRoute) {
    return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Sprint 10.2: Allow public routes without authentication
    if (!isAuthenticated && !isPublicRoute) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }
    
    // Sprint 10.2: Render public routes if on a public page
    if (isPublicRoute) {
        return (
            <>
                {/* Public routes accessible without authentication */}
                <Routes>
                    <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
                    <Route path="/reset-password" element={<RequestPasswordReset />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                </Routes>
            </>
        );
    }
    
    // Sprint 10.2: Check if user needs to change password (force_password_change)
    const handlePasswordChanged = () => {
        // Refresh user data to clear force_password_change flag
        checkAuthentication();
    };

    // Sprint 13: Navigation handlers using navigate (navigate is declared at top of component)
    const handleCitySelect = (cityName) => {
        navigate(`/city/${encodeURIComponent(cityName)}`);
    };

    // Sprint 20: Refactored to use RFQTableView component
    function MainRFQView() {
        return (
            <RFQTableView
                rfqs={rfqs}
                cities={cities}
                healthData={healthData}
                userRole={userRole}
                updateUserStatus={updateUserStatus}
                updateWorkType={updateWorkType}
            />
        );
    }

    // Old MainRFQView - kept temporarily for reference, will be removed after testing
    function MainRFQView_OLD() {
        return (
            <div className="container mt-4">

            {/* Health Alerts - Dev Only */}
            {userRole === 'dev' && healthData && healthData.alerts && healthData.alerts.length > 0 && (
                <div className="alert-container mb-4">
                    <h6 className="text-muted small mb-2">Developer Information:</h6>
                    {healthData.has_critical_alerts && (
                        <div className="alert alert-danger">
                            <h5 className="alert-heading">🚨 Critical Issues Detected</h5>
                            <ul className="mb-0">
                                {healthData.alerts.filter(a => a.severity === 'critical').map((alert, idx) => (
                                    <li key={idx}><strong>{alert.city}:</strong> {alert.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {healthData.has_warnings && !healthData.has_critical_alerts && (
                        <div className="alert alert-warning">
                            <h5 className="alert-heading">⚠️ Scraper Warnings</h5>
                            <ul className="mb-0">
                                {healthData.alerts.filter(a => a.severity === 'warning' || a.severity === 'error').map((alert, idx) => (
                                    <li key={idx}><strong>{alert.city}:</strong> {alert.message}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {healthData.timestamp && (
                        <div className="text-muted small">
                            Last scrape: {new Date(healthData.timestamp).toLocaleString()} | 
                            Cities: {healthData.cities_scraped} | 
                            Success: {healthData.success_count} | 
                            Failed: {healthData.failed_count}
                        </div>
                    )}
                </div>
            )}

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Filters</h5>
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label">Search</label>
                            <input 
                                ref={searchInputRef}
                                id="rfq-search-input"
                                type="text" 
                                className="form-control" 
                                placeholder="Search title, number, org..."
                                value={searchInput}
                                onChange={e => setSearchInput(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Work Type</label>
                            <select 
                                className="form-select"
                                value={filters.workType}
                                onChange={e => setFilters({...filters, workType: e.target.value})}
                            >
                                <option value="all">All Types</option>
                                {WORK_TYPES.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Status</label>
                            <select 
                                className="form-select"
                                value={filters.userStatus}
                                onChange={e => setFilters({...filters, userStatus: e.target.value})}
                            >
                                <option value="all">All Status</option>
                                <option value="new">New</option>
                                <option value="consider">Consider</option>
                                <option value="pursuing">Pursuing</option>
                                <option value="watch">Watch</option>
                                <option value="completed">Completed</option>
                                <option value="lost">Lost</option>
                                <option value="declined">Declined</option>
                                <option value="ignore">Ignored</option>
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Organization</label>
                            <select 
                                className="form-select"
                                value={filters.organization}
                                onChange={e => setFilters({...filters, organization: e.target.value})}
                            >
                                <option value="all">All Organizations</option>
                                {organizations.map(org => (
                                    <option key={org} value={org}>{org}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Source</label>
                            <select 
                                className="form-select"
                                value={filters.source}
                                onChange={e => setFilters({...filters, source: e.target.value})}
                            >
                                <option value="all">All Sources</option>
                                <option value="scraper">🌐 Web Scraper</option>
                                <option value="email">📧 Email</option>
                                <option value="manual">✏️ Manual Entry</option>
                            </select>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <div className="form-check">
                                <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id="hideIgnored"
                                    checked={filters.hideIgnored}
                                    onChange={e => setFilters({...filters, hideIgnored: e.target.checked})}
                                />
                                <label className="form-check-label" htmlFor="hideIgnored">
                                    Hide Ignored
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="row g-3 mt-2">
                        <div className="col-md-3">
                            <label className="form-label">Sort By</label>
                            <select 
                                className="form-select"
                                value={filters.sortBy}
                                onChange={e => setFilters({...filters, sortBy: e.target.value})}
                            >
                                <option value="due_date">Due Date (Soonest First)</option>
                                <option value="date_added">Date Added (Newest First)</option>
                                <option value="organization">Organization (A-Z)</option>
                                <option value="work_type">Work Type (A-Z)</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-2">
                        <span className="text-muted">
                            Showing {filteredRfqs.length} of {rfqs.length} RFQs
                        </span>
                        {filteredRfqs.length !== rfqs.length && (
                            <button 
                                className="btn btn-sm btn-link" 
                                onClick={() => {
                                    setSearchInput('');
                                    setFilters({workType: 'all', userStatus: 'all', organization: 'all', searchTerm: '', hideIgnored: false});
                                }}
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* RFQs Table */}
            <div className="card">
                <div className="card-body">
                    <h5 className="card-title">RFQs</h5>
                    
                    {/* Sprint 15: Mobile Card View */}
                    <div className="d-md-none">
                        {filteredRfqs.length === 0 ? (
                            <div className="text-center text-muted py-4">
                                No RFQs match your filters
                            </div>
                        ) : (
                            filteredRfqs.map((rfq) => (
                                <div key={rfq.job_id || rfq.rfp_number} className="mobile-job-card">
                                    <div className="mobile-job-header">
                                        <div>
                                            <h3 className="mobile-job-title">{rfq.title}</h3>
                                            <p className="mobile-job-org">
                                                <a 
                                                    href="#" 
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCitySelect(rfq.organization);
                                                    }}
                                                    style={{color: 'var(--text-secondary)', textDecoration: 'none'}}
                                                >
                                                    {rfq.organization}
                                                </a>
                                                {rfq.source && rfq.source === 'email' && (
                                                    <span 
                                                        className="job-source-badge"
                                                        style={{
                                                            marginLeft: '8px',
                                                            padding: '2px 8px',
                                                            borderRadius: '12px',
                                                            backgroundColor: '#4CAF50',
                                                            color: 'white',
                                                            fontSize: '11px',
                                                            fontWeight: '500'
                                                        }}
                                                        title={`Added from email on ${rfq.first_seen || 'unknown date'}`}
                                                    >
                                                        📧 Email
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px'}}>
                                            <span 
                                                className="mobile-job-status"
                                                style={{
                                                    backgroundColor: 
                                                        rfq.user_status === 'consider' ? 'var(--status-consider)' :
                                                        rfq.user_status === 'pursuing' ? 'var(--status-pursuing)' :
                                                        rfq.user_status === 'watch' ? 'var(--status-watch)' :
                                                        rfq.user_status === 'completed' ? 'var(--status-completed)' :
                                                        rfq.user_status === 'lost' ? 'var(--status-lost)' :
                                                        rfq.user_status === 'ignore' ? 'var(--status-ignored)' :
                                                        'var(--status-new)',
                                                    color: 'white'
                                                }}
                                            >
                                                {rfq.user_status === 'new' && 'NEW'}
                                                {rfq.user_status === 'consider' && 'Consider'}
                                                {rfq.user_status === 'pursuing' && 'Pursuing'}
                                                {rfq.user_status === 'watch' && 'Watch'}
                                                {rfq.user_status === 'completed' && 'Completed'}
                                                {rfq.user_status === 'lost' && 'Lost'}
                                                {rfq.user_status === 'declined' && 'Declined'}
                                                {rfq.user_status === 'ignore' && 'Ignored'}
                                            </span>
                                            {rfq.first_seen && (
                                                <span style={{fontSize: '10px', color: 'var(--text-secondary)', whiteSpace: 'nowrap'}}>
                                                    Added {rfq.first_seen}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="mobile-job-details">
                                        <div className="mobile-job-detail">
                                            <div className="mobile-job-detail-label">Due Date</div>
                                            <div className="mobile-job-detail-value" style={getDueDateStyle(rfq.due_date)}>
                                                {rfq.due_date}
                                            </div>
                                        </div>
                                        <div className="mobile-job-detail">
                                            <div className="mobile-job-detail-label">Work Type</div>
                                            <div className="mobile-job-detail-value">
                                                {rfq.work_type || 'Unknown'}
                                            </div>
                                        </div>
                                        <div className="mobile-job-detail">
                                            <div className="mobile-job-detail-label">RFP Number</div>
                                            <div className="mobile-job-detail-value">{rfq.rfp_number}</div>
                                        </div>
                                        <div className="mobile-job-detail">
                                            <div className="mobile-job-detail-label">Job ID</div>
                                            <div className="mobile-job-detail-value">
                                                <a
                                                    href={`/job/${rfq.job_id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(`/job/${rfq.job_id}`);
                                                    }}
                                                    style={{color: 'var(--primary)', textDecoration: 'none', fontFamily: 'monospace'}}
                                                >
                                                    {rfq.job_id ? rfq.job_id.substring(0, 8) : 'N/A'}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mobile-job-actions">
                                        <button 
                                            className="mobile-job-action-btn primary" 
                                            onClick={() => {
                                                navigate(`/job/${rfq.job_id}`);
                                            }}
                                        >
                                            📋 View Details
                                        </button>
                                        <button 
                                            className="mobile-job-action-btn" 
                                            onClick={() => updateUserStatus(rfq.job_id, 'pursuing')}
                                        >
                                            ✓ Pursue
                                        </button>
                                        <button 
                                            className="mobile-job-action-btn" 
                                            onClick={() => updateUserStatus(rfq.job_id, 'watch')}
                                        >
                                            👁️ Watch
                                        </button>
                                        {rfq.link && (
                                            <a 
                                                href={rfq.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mobile-job-action-btn"
                                                style={{textDecoration: 'none', textAlign: 'center'}}
                                            >
                                                🔗 RFQ Link
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/* Sprint 15: Desktop Table View */}
                    <div className="table-responsive d-none d-md-block desktop-table">
                        <table className="table table-hover">
                <thead>
                    <tr>
                                    <th>Status</th>
                                    <th>Job ID</th>
                        <th>Organization</th>
                        <th>Title</th>
                        <th>Work Type</th>
                        <th>Due Date</th>
                                    <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                                {filteredRfqs.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            No RFQs match your filters
                                        </td>
                        </tr>
                                ) : (
                                    filteredRfqs.map((rfq) => (
                                        <tr key={rfq.job_id || rfq.rfp_number} className={rfq.user_status === 'new' ? 'table-primary' : ''}>
                                            <td>
                                                {rfq.user_status === 'new' && <span className="badge bg-success">NEW</span>}
                                                {rfq.user_status === 'consider' && <span className="badge bg-warning">Consider</span>}
                                                {rfq.user_status === 'pursuing' && <span className="badge bg-warning">Pursuing</span>}
                                                {rfq.user_status === 'watch' && <span className="badge bg-primary">Watch</span>}
                                                {rfq.user_status === 'completed' && <span className="badge bg-info">Completed</span>}
                                                {rfq.user_status === 'lost' && <span className="badge bg-dark">Lost</span>}
                                                {rfq.user_status === 'declined' && <span className="badge bg-secondary">Declined</span>}
                                                {rfq.user_status === 'ignore' && <span className="badge bg-danger">Ignored</span>}
                                            </td>
                                            <td>
                                                <a
                                                    href={`/job/${rfq.job_id}`}
                                                    className="font-monospace text-decoration-none"
                                                    title={`View details for ${rfq.job_id}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        navigate(`/job/${rfq.job_id}`);
                                                    }}
                                                >
                                                    {rfq.job_id ? rfq.job_id.substring(0, 8) : 'N/A'}
                                                </a>
                                            </td>
                                            <td>
                                                <small>
                                                    <a 
                                                        href="#" 
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleCitySelect(rfq.organization);
                                                        }}
                                                        className="text-decoration-none"
                                                    >
                                                        {rfq.organization}
                                                    </a>
                                                </small>
                                                {getCityUrl(rfq.organization) && (
                                                    <div>
                                                        <a 
                                                            href={getCityUrl(rfq.organization)} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="text-decoration-none"
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            🔗 Procurement Page
                                                        </a>
                                                    </div>
                                                )}
                                            </td>
                                            <td>
                                                <a href={rfq.link || '#'} target="_blank" rel="noopener noreferrer">
                                                    {rfq.title}
                                                </a>
                                                <br />
                                                <small className="text-muted">Org #: {rfq.rfp_number}</small>
                                            </td>
                                            <td>
                                                <select 
                                                    className="form-select form-select-sm"
                                                    value={rfq.work_type || 'Unknown'}
                                                    onChange={(e) => updateWorkType(rfq.job_id, e.target.value)}
                                                    style={{minWidth: '150px'}}
                                                >
                                                    {WORK_TYPES.map(type => (
                                                        <option key={type} value={type}>{type}</option>
                                                    ))}
                                                </select>
                                            </td>
                            <td style={getDueDateStyle(rfq.due_date)}>{rfq.due_date}</td>
                                            <td>
                                                <div className="btn-group btn-group-sm" role="group">
                                                    <button 
                                                        className="btn btn-outline-success" 
                                                        onClick={() => updateUserStatus(rfq.job_id, 'pursuing')}
                                                        title="Mark as Pursuing"
                                                    >
                                                        ✓
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-info" 
                                                        onClick={() => updateUserStatus(rfq.job_id, 'completed')}
                                                        title="Mark as Completed"
                                                    >
                                                        ✔✔
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-primary" 
                                                        onClick={() => updateUserStatus(rfq.job_id, 'watch')}
                                                        title="Watch (for relationship building)"
                                                    >
                                                        👁️
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-dark" 
                                                        onClick={() => updateUserStatus(rfq.job_id, 'lost')}
                                                        title="Mark as Lost"
                                                    >
                                                        ✖
                                                    </button>
                                                    <button 
                                                        className="btn btn-outline-danger" 
                                                        onClick={() => updateUserStatus(rfq.job_id, 'ignore')}
                                                        title="Ignore this RFQ"
                                                    >
                                                        ✗
                                                    </button>
                                                    <a 
                                                        href={`${API_BASE_URL}/generate_calendar/${rfq.job_id}`}
                                                        className="btn btn-outline-success"
                                                        title="Add to Calendar (downloads .ics file)"
                                                        download
                                                    >
                                                        📅
                                                    </a>
                                                </div>
                                            </td>
                        </tr>
                                    ))
                                )}
                </tbody>
            </table>
                    </div>
                    {/* End Desktop Table */}
                </div>
            </div>

            {/* Admin Section - Collapsed by default */}
            <div className="card mt-4">
                <div className="card-body">
                    <details>
                        <summary className="fw-bold" style={{cursor: 'pointer'}}>
                            Advanced Options
                        </summary>
                        <div className="mt-3">
                            <h5>Add City</h5>
                            <div className="row g-2 mb-3">
                                <div className="col-md-4">
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="Organization" 
                                        onChange={e => setNewCity({...newCity, organization: e.target.value})} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        placeholder="URL" 
                                        onChange={e => setNewCity({...newCity, url: e.target.value})} 
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button 
                                        className="btn btn-primary w-100" 
                                        onClick={addCity}
                                        disabled={detectionLoading}
                                    >
                                        {detectionLoading ? '🔍 Detecting...' : '➕ Add City'}
                                    </button>
                                </div>
                            </div>

                            <hr />

                            <h5>📊 Bulk Upload Cities</h5>
                            <div className="mb-3">
                                <p className="small text-muted">
                                    Upload a CSV or Excel file with columns: <code>organization</code>, <code>url</code> (required).
                                    Optional: <code>row_selector</code>, <code>cell_count</code>, <code>is_dynamic</code>, <code>manual</code>
                                </p>
                                <input 
                                    type="file" 
                                    className="form-control mb-2" 
                                    accept=".csv,.xlsx,.xls"
                                    onChange={(e) => handleBulkCityUpload(e.target.files[0])}
                                />
                            </div>

                            <hr />

                            <h5>Manual Input</h5>
                            <div className="mb-3">
                                <input 
                                    type="text" 
                                    className="form-control mb-2" 
                                    placeholder="Organization Name (e.g., City of Chandler)" 
                                    value={screenshotOrg}
                                    onChange={e => setScreenshotOrg(e.target.value)} 
                                />
                                <input 
                                    type="url" 
                                    className="form-control mb-2" 
                                    placeholder="Job URL (e.g., https://procurement.az.gov/...)" 
                                    value={pasteUrl}
                                    onChange={e => setPasteUrl(e.target.value)} 
                                />
                                <textarea 
                                    className="form-control mb-2" 
                                    placeholder="Paste RFQ text here (Project number, title, due date, description...)" 
                                    value={pasteText} 
                                    onChange={e => setPasteText(e.target.value)} 
                                    rows="4"
                                />
                                <button className="btn btn-secondary" onClick={parseText}>Parse Text</button>
                            </div>

                            <hr />

                            <h5>Upload Screenshot</h5>
                            <div className="mb-3">
                                <input 
                                    type="file" 
                                    className="form-control mb-2" 
                                    accept="image/*" 
                                    onChange={e => setScreenshot(e.target.files[0])} 
                                />
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={uploadScreenshot} 
                                    disabled={!screenshot}
                                >
                                    Upload Screenshot
                                </button>
                            </div>
                        </div>
                    </details>
                </div>
            </div>
            
            {/* Configuration Review Modal */}
            {showConfigModal && detectedConfig && (
                <div 
                    className="modal show d-block" 
                    tabIndex="-1" 
                    style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
                    onClick={() => {
                        if (window.confirm('Cancel city configuration?')) {
                            setShowConfigModal(false);
                            setDetectedConfig(null);
                        }
                    }}
                >
                    <div 
                        className="modal-dialog modal-lg modal-dialog-centered"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="modal-content" style={{
                            backgroundColor: theme === 'dark' ? '#2b3035' : '#fff',
                            color: theme === 'dark' ? '#e0e0e0' : '#000'
                        }}>
                            <div className="modal-header" style={{
                                borderBottom: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6'
                            }}>
                                <h5 className="modal-title">
                                    🔍 Review Detected Configuration
                                </h5>
                                <button 
                                    type="button" 
                                    className={theme === 'dark' ? 'btn-close btn-close-white' : 'btn-close'}
                                    onClick={() => {
                                        setShowConfigModal(false);
                                        setDetectedConfig(null);
                                    }}
                                ></button>
                            </div>
                            <div className="modal-body">
                                {/* Confidence Badge */}
                                <div className="mb-3">
                                    <span className={`badge ${
                                        detectedConfig.confidence === 'high' ? 'bg-success' :
                                        detectedConfig.confidence === 'medium' ? 'bg-warning' :
                                        detectedConfig.confidence === 'low' ? 'bg-secondary' :
                                        'bg-danger'
                                    } me-2`}>
                                        Confidence: {detectedConfig.confidence || 'unknown'}
                                    </span>
                                    {detectedConfig.uses_cloudflare && (
                                        <span className="badge bg-warning">⚠️ Uses Cloudflare</span>
                                    )}
                                </div>
                                
                                {/* Detection Notes */}
                                {detectedConfig.notes && detectedConfig.notes.length > 0 && (
                                    <div className="alert alert-info mb-3" style={{
                                        backgroundColor: theme === 'dark' ? 'rgba(13, 202, 240, 0.15)' : '#d1ecf1',
                                        borderColor: theme === 'dark' ? 'rgba(13, 202, 240, 0.3)' : '#bee5eb',
                                        color: theme === 'dark' ? '#9eeaf9' : '#0c5460'
                                    }}>
                                        <strong>📋 Detection Notes:</strong>
                                        <ul className="mb-0 mt-2">
                                            {detectedConfig.notes.map((note, idx) => (
                                                <li key={idx}>{note}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                
                                {/* Editable Configuration Fields */}
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label fw-bold">Organization</label>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            value={detectedConfig.organization || ''}
                                            onChange={(e) => setDetectedConfig({
                                                ...detectedConfig, 
                                                organization: e.target.value
                                            })}
                                        />
                                    </div>
                                    
                                    <div className="col-12">
                                        <label className="form-label fw-bold">URL</label>
                                        <input 
                                            type="text" 
                                            className="form-control"
                                            value={detectedConfig.url || ''}
                                            onChange={(e) => setDetectedConfig({
                                                ...detectedConfig, 
                                                url: e.target.value
                                            })}
                                        />
                                    </div>
                                    
                                    <div className="col-8">
                                        <label className="form-label fw-bold">Row Selector (CSS)</label>
                                        <input 
                                            type="text" 
                                            className="form-control font-monospace"
                                            value={detectedConfig.row_selector || ''}
                                            onChange={(e) => setDetectedConfig({
                                                ...detectedConfig, 
                                                row_selector: e.target.value
                                            })}
                                            placeholder="tbody tr"
                                        />
                                    </div>
                                    
                                    <div className="col-4">
                                        <label className="form-label fw-bold">Cell Count</label>
                                        <input 
                                            type="number" 
                                            className="form-control"
                                            value={detectedConfig.cell_count || 4}
                                            onChange={(e) => setDetectedConfig({
                                                ...detectedConfig, 
                                                cell_count: parseInt(e.target.value)
                                            })}
                                        />
                                    </div>
                                    
                                    <div className="col-6">
                                        <div className="form-check">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input"
                                                id="isDynamic"
                                                checked={detectedConfig.is_dynamic || false}
                                                onChange={(e) => setDetectedConfig({
                                                    ...detectedConfig, 
                                                    is_dynamic: e.target.checked
                                                })}
                                            />
                                            <label className="form-check-label" htmlFor="isDynamic">
                                                Dynamic (JavaScript-rendered)
                                            </label>
                                        </div>
                                        <small className="text-muted d-block mt-1">
                                            ✅ Check if page uses JS/AJAX to load jobs (Bonfire, OpenGov, etc.)
                                        </small>
                                    </div>
                                    
                                    <div className="col-6">
                                        <div className="form-check">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input"
                                                id="isManual"
                                                checked={detectedConfig.manual || false}
                                                onChange={(e) => setDetectedConfig({
                                                    ...detectedConfig, 
                                                    manual: e.target.checked
                                                })}
                                            />
                                            <label className="form-check-label" htmlFor="isManual">
                                                Manual Entry Only (Skip Scraping)
                                            </label>
                                        </div>
                                        <small className="text-muted d-block mt-1">
                                            ✅ Check if city requires manual job entry (scraper will skip, but remind you)
                                        </small>
                                    </div>
                                    
                                    <div className="col-12">
                                        <div className="alert alert-info mt-3 mb-0" style={{
                                            backgroundColor: theme === 'dark' ? 'rgba(13, 110, 253, 0.15)' : '#cfe2ff',
                                            borderColor: theme === 'dark' ? 'rgba(13, 110, 253, 0.3)' : '#9ec5fe',
                                            color: theme === 'dark' ? '#9ec5fe' : '#084298'
                                        }}>
                                            <strong>💡 Tips:</strong>
                                            <ul className="mb-0 mt-2 small">
                                                <li><strong>Cell Count</strong>: Number of columns in the job table. If no jobs are visible now, try "4" and test later.</li>
                                                <li><strong>Row Selector</strong>: CSS selector to find job rows (e.g., "tbody tr" for standard tables).</li>
                                                <li><strong>Dynamic</strong>: Check this for Bonfire, OpenGov, or any site that loads jobs with JavaScript.</li>
                                                <li><strong>Manual Only</strong>: Check this if the site is too complex or requires login (scraper will remind you to check manually).</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer" style={{
                                borderTop: theme === 'dark' ? '1px solid #495057' : '1px solid #dee2e6'
                            }}>
                                <button 
                                    type="button" 
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setShowConfigModal(false);
                                        setDetectedConfig(null);
                                    }}
                                >
                                    ❌ Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary"
                                    onClick={() => {
                                        // Remove detection-specific fields before saving
                                        const cityConfig = {
                                            organization: detectedConfig.organization,
                                            url: detectedConfig.url,
                                            row_selector: detectedConfig.row_selector,
                                            cell_count: detectedConfig.cell_count,
                                            is_dynamic: detectedConfig.is_dynamic || false,
                                            manual: detectedConfig.manual || false
                                        };
                                        confirmAndSaveCity(cityConfig);
                                    }}
                                >
                                    ✅ Save City
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
    } // End of MainRFQView_OLD (kept temporarily for rollback if needed)
    
    // Sprint 13: Define all routes
    return (
        <>
            {/* Sprint 10.2: Force Password Change Modal */}
            {currentUser && currentUser.force_password_change && (
                <ForcePasswordChange 
                    onPasswordChanged={handlePasswordChanged}
                    onLogout={handleLogout}
                />
            )}
            
            {/* Sprint 15: Sidebar Navigation */}
            {sidebarOpen && <div className="sidebar-overlay active" onClick={closeSidebar}></div>}
            
            <nav className={`sidebar-nav ${sidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    {companySettings?.logo_url && (
                        <img 
                            src={`${API_BASE_URL}${companySettings.logo_url}`} 
                            alt="Company Logo" 
                            className="company-logo"
                            style={{height: '40px', marginRight: '10px', objectFit: 'contain'}}
                        />
                    )}
                    <h3>RFQ Tracker</h3>
                    <button className="sidebar-close" onClick={closeSidebar} aria-label="Close menu">
                        ✕
                    </button>
                </div>
                
                <ul className="sidebar-nav-list">
                    {/* Main Navigation */}
                    <li className="sidebar-section-title">Main</li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/')}>
                            📋 RFQ List
                        </button>
                    </li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/calendar')}>
                            📅 Calendar View
                        </button>
                    </li>
                    {/* Sprint 16: My Cities - All users can see their company's cities */}
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/cities')}>
                            📍 My Cities
                        </button>
                    </li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/contacts')}>
                            📇 Contacts
                        </button>
                    </li>
                    <li className="sidebar-nav-item">
                        <a className="sidebar-nav-button" href="https://help.rfq-tracker.com" target="_blank" rel="noopener noreferrer" style={{textDecoration: 'none', color: 'inherit', display: 'block'}}>
                            ❓ Help
                        </a>
                    </li>
                    
                    {/* Actions */}
                    <li className="sidebar-section-title">Actions</li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => { handleExportToExcel(); closeSidebar(); }}>
                            📊 Export to Excel
                        </button>
                    </li>
                    
                    {/* Dev Only */}
                    {userRole === 'dev' && (
                        <>
                            <li className="sidebar-section-title">Developer</li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/add-client')}>
                                    ➕ Add New RFQ Tracker Client
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => { runScraper(); closeSidebar(); }}>
                                    🔄 Run Scraper
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => { syncDatabase(); }}>
                                    💾 Sync Database
                                </button>
                            </li>
                        </>
                    )}
                    
                    {/* Admin/Dev Only */}
                    {(userRole === 'dev' || userRole === 'admin') && (
                        <>
                            <li className="sidebar-section-title">Administration</li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/users')}>
                                    👥 User Management
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/company/cities')}>
                                    📍 Tracked Cities
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/audit-logs')}>
                                    📊 Audit Logs
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/conflicts')}>
                                    ⚠️ Review Conflicts
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/new-sources')}>
                                    📧 New RFQ Sources
                                </button>
                            </li>
                            <li className="sidebar-nav-item">
                                <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/ai-monitoring')}>
                                    🤖 AI System Health
                                </button>
                            </li>
                        </>
                    )}
                    
                    {/* Account */}
                    <li className="sidebar-section-title">Account</li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/notifications')}>
                            📧 Email Notifications
                        </button>
                    </li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => navigateAndCloseSidebar('/settings')}>
                            ⚙️ Change Password
                        </button>
                    </li>
                    <li className="sidebar-nav-item">
                        <button className="sidebar-nav-button" onClick={() => { handleLogout(); closeSidebar(); }} style={{ color: '#dc2626' }}>
                            🚪 Logout
                        </button>
                    </li>
                </ul>
            </nav>

            {/* Sprint 15: Clean Top Navigation Bar */}
            <div className="container-fluid px-3 py-3" style={{backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)'}}>
                <div className="d-flex justify-content-between align-items-center">
                    {/* Left: Hamburger + Logo */}
                    <div className="d-flex align-items-center gap-3">
                        <button 
                            className={`hamburger-btn ${sidebarOpen ? 'active' : ''}`}
                            onClick={toggleSidebar}
                            aria-label="Toggle navigation menu"
                        >
                            <div className="hamburger-icon">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </button>
                        {companySettings?.logo_url && (
                            <img 
                                src={`${API_BASE_URL}${companySettings.logo_url}`} 
                                alt="Company Logo" 
                                className="company-logo"
                                style={{height: '40px', marginRight: '10px', objectFit: 'contain'}}
                            />
                        )}
                        <h1 className="mb-0" style={{fontSize: '1.5rem', fontWeight: 600}}>RFQ Tracker</h1>
                    </div>
                    
                    {/* Right: Theme Toggle + User Menu */}
                    <div className="d-flex gap-2 align-items-center">
                        <button 
                            onClick={toggleTheme} 
                            className="theme-toggle"
                            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
                            aria-label="Toggle theme"
                        >
                            {theme === 'light' ? '🌙' : '☀️'}
                        </button>
                        
                        {currentUser && (
                            <div className="d-flex align-items-center gap-2">
                                <span className="badge" style={{backgroundColor: 'var(--primary)', padding: '6px 12px', fontSize: '0.875rem'}}>
                                    {userRole === 'dev' && '🛠️ Dev'}
                                    {userRole === 'admin' && '🔑 Admin'}
                                    {userRole === 'manager' && '📊 Manager'}
                                    {userRole === 'viewer' && '👀 Viewer'}
                                </span>
                                <span className="text-muted small desktop-only">{currentUser.username}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

        <Routes>
            {/* Job details route */}
            <Route path="/job/:jobId" element={<JobDetailsRoute />} />
            
            {/* Calendar view route */}
            <Route path="/calendar" element={
                <CalendarView 
                    onBack={() => navigate('/')}
                    onJobSelect={(jobId) => navigate(`/job/${jobId}`)}
                    theme={theme}
                    toggleTheme={toggleTheme}
                />
            } />
            
            {/* Cities list route - Sprint 20: Refactored with DataTable */}
            <Route path="/cities" element={
                <CitiesTableView 
                    userRole={userRole}
                    isAdmin={['dev', 'admin'].includes(userRole)}
                    onCitySelect={handleCitySelect}
                    theme={theme}
                />
            } />
            
            {/* City profile route */}
            <Route path="/city/:cityName" element={<CityProfileRoute />} />
            
            {/* Contacts list route - Sprint 20: Refactored with DataTable */}
            <Route path="/contacts" element={
                <ContactsTableView 
                    onViewContact={(contactId) => navigate(`/contact/${contactId}`)}
                    theme={theme}
                />
            } />
            
            {/* Add Contact route - Uses old ContactsList with add form */}
            <Route path="/contacts/add" element={
                <ContactsList 
                    onViewContact={(contactId) => navigate(`/contact/${contactId}`)}
                    onBack={() => navigate('/contacts')}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    initialMode="add"
                />
            } />
            
            {/* Import Contacts CSV route - Uses old ContactsList with import wizard */}
            <Route path="/contacts/import" element={
                <ContactsList 
                    onViewContact={(contactId) => navigate(`/contact/${contactId}`)}
                    onBack={() => navigate('/contacts')}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    initialMode="import"
                />
            } />
            
            {/* Contact Settings route - Uses old ContactsList with settings form */}
            <Route path="/contacts/settings" element={
                <ContactsList 
                    onViewContact={(contactId) => navigate(`/contact/${contactId}`)}
                    onBack={() => navigate('/contacts')}
                    theme={theme}
                    toggleTheme={toggleTheme}
                    initialMode="settings"
                />
            } />
            
            {/* Contact profile route */}
            <Route path="/contact/:contactId" element={<ContactProfileRoute />} />
            
            {/* User Management route (Admin/Dev only) */}
            <Route path="/users" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <div className="container mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <UserManagement 
                            currentUser={currentUser}
                            theme={theme}
                            toggleTheme={toggleTheme}
                        />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Audit Logs route (Admin/Dev only) */}
            <Route path="/audit-logs" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <div className="container mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <AuditLogViewer 
                            currentUser={currentUser}
                            theme={theme}
                            toggleTheme={toggleTheme}
                        />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Job Conflicts route (Dev only) - Conflict Resolution Feature - Sprint 20: Refactored with DataTable */}
            <Route path="/conflicts" element={
                userRole === 'dev' ? (
                    <div className="container mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <JobConflictsTableView currentUser={currentUser} />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* New RFQ Sources route (Admin/Dev only) - Sprint 19 Email Watcher */}
            <Route path="/new-sources" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <div className="container-fluid mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <NewSourcesTableView />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* AI Monitoring route (Admin/Dev only) - Sprint 19 */}
            <Route path="/ai-monitoring" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <div className="container-fluid mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <AIMonitoring />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Company Cities route (Admin/Dev only) - Sprint 16 */}
            <Route path="/company/cities" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <div className="container mt-4">
                        <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                            ← Back to RFQs
                        </button>
                        <CompanyCities 
                            currentUser={currentUser}
                            theme={theme}
                            toggleTheme={toggleTheme}
                        />
                    </div>
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Settings/Change Password route (All users) */}
            <Route path="/settings" element={
                <div className="container mt-4">
                    <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                        ← Back to RFQs
                    </button>
                    <ChangePassword 
                        currentUser={currentUser}
                        theme={theme}
                        toggleTheme={toggleTheme}
                    />
                </div>
            } />
            
            {/* Email Notifications route (Sprint 14 - All users) */}
            <Route path="/notifications" element={
                <div className="container mt-4">
                    <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                        ← Back to RFQs
                    </button>
                    <NotificationSettings 
                        currentUser={currentUser}
                        theme={theme}
                        toggleTheme={toggleTheme}
                    />
                </div>
            } />
            
            {/* Onboarding Wizard route (Admin only - Phase 2) */}
            <Route path="/onboarding" element={
                (userRole === 'dev' || userRole === 'admin') ? (
                    <OnboardingWizard />
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Admin or Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Add Client route (Dev only - Phase 2) */}
            <Route path="/add-client" element={
                userRole === 'dev' ? (
                    <AddClientForm navigate={navigate} theme={theme} />
                ) : (
                    <div className="container mt-4">
                        <div className="alert alert-danger">
                            Access denied. Dev role required.
                        </div>
                        <button onClick={() => navigate('/')} className="btn btn-secondary">
                            ← Back to RFQs
                        </button>
                    </div>
                )
            } />
            
            {/* Main RFQ list route (default) */}
            <Route path="/" element={<MainRFQView />} />
        </Routes>
        </>
    );
}

// Sprint 13: Route wrapper components that use URL parameters
function JobDetailsRoute() {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const [theme] = useState(() => localStorage.getItem('rfq-theme') || 'light');
    
    return (
        <JobDetails 
            jobId={jobId}
            theme={theme}
            toggleTheme={() => {}}
            onBack={() => navigate('/')}
        />
    );
}

function CityProfileRoute() {
    const { cityName } = useParams();
    const navigate = useNavigate();
    const [theme] = useState(() => localStorage.getItem('rfq-theme') || 'light');
    
    return (
        <CityProfile 
            cityName={decodeURIComponent(cityName)}
            onBack={() => navigate('/cities')}
            onViewJobs={(city) => navigate(`/?city=${encodeURIComponent(city)}`)}
            onSelectJob={(jobId) => navigate(`/job/${jobId}`)}
            theme={theme}
            toggleTheme={() => {}}
        />
    );
}

function ContactProfileRoute() {
    const { contactId } = useParams();
    const navigate = useNavigate();
    const [theme] = useState(() => localStorage.getItem('rfq-theme') || 'light');
    
    return (
        <ContactProfile 
            contactId={contactId}
            onBack={() => navigate('/contacts')}
            onViewJob={(jobId) => navigate(`/job/${jobId}`)}
            theme={theme}
            toggleTheme={() => {}}
        />
    );
}

// Phase 2: Add Client Form Component (Dev only)
function AddClientForm({ navigate, theme }) {
    const [companyName, setCompanyName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/companies/create`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    company_name: companyName,
                    admin_email: adminEmail
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.detail || 'Failed to create client');
            }

            const data = await response.json();
            setSuccess(data.message);
            setCompanyName('');
            setAdminEmail('');
            
            // Show success for 2 seconds then redirect
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (err) {
            setError(err.message || 'Failed to create client');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container mt-4">
            <button onClick={() => navigate('/')} className="btn btn-secondary mb-3">
                ← Back to RFQs
            </button>
            
            <div className="card" style={{
                backgroundColor: theme === 'dark' ? '#2b3035' : '#fff',
                color: theme === 'dark' ? '#e0e0e0' : '#000'
            }}>
                <div className="card-body">
                    <h2 className="card-title">Add New RFQ Tracker Client</h2>
                    <p className="text-muted">
                        Create a new client company and send setup invitation to their admin.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label htmlFor="companyName" className="form-label">
                                Company Name <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="text"
                                id="companyName"
                                className="form-control"
                                placeholder="e.g., Company 2 - Tempe Office"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                required
                            />
                        </div>

                        <div className="mb-3">
                            <label htmlFor="adminEmail" className="form-label">
                                Admin Email <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                                type="email"
                                id="adminEmail"
                                className="form-control"
                                placeholder="admin@company.com"
                                value={adminEmail}
                                onChange={(e) => setAdminEmail(e.target.value)}
                                required
                            />
                            <small className="form-text text-muted">
                                An invitation email will be sent to this address
                            </small>
                        </div>

                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="alert alert-success" role="alert">
                                ✅ {success}
                                <br />
                                <small>Redirecting to RFQ list...</small>
                            </div>
                        )}

                        <div className="d-flex gap-2">
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading || !companyName || !adminEmail}
                            >
                                {loading ? 'Creating Client...' : 'Create Client & Send Invitation'}
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate('/')}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>

                    <div className="mt-4 p-3 bg-light rounded" style={{
                        backgroundColor: theme === 'dark' ? '#1a1d21' : '#f8f9fa',
                        borderColor: theme === 'dark' ? '#495057' : '#dee2e6'
                    }}>
                        <h5>What happens next?</h5>
                        <ol className="mb-0">
                            <li>A new company stub will be created in the database</li>
                            <li>An invitation email will be sent to the admin</li>
                            <li>When the admin accepts the invitation and logs in for the first time, they'll be automatically redirected to the onboarding wizard</li>
                            <li>The admin completes the wizard to set up their company</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Main App wrapper with BrowserRouter
function AppWrapper() {
    return (
        <BrowserRouter>
            <App />
        </BrowserRouter>
    );
}

export default AppWrapper;